import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, combineLatest, filter, map, of, switchMap } from 'rxjs';

import { NgCommService } from '../../ajs-ng-communication.service';
import { AppSelector } from '../../core/store';
import { FormulaRequester } from '../../requesters/formula/formula.requester';
import {
	FormulaAction,
	FormulaActionPopupComponent,
	IFormulaActionModalData,
} from '../components/formula-action-popup/formula-action-popup.component';
import { FormulaParser } from '../formula.parser';
import {
	bulkDeleteFormula,
	deleteFormula,
	deleteFormulaFail,
	duplicateFormula,
	duplicateFormulaFail,
	fetchFormulaListFail,
	fetchFormulaListSuccess,
	fetchFormulaUsage,
	fetchFormulaUsageFail,
	fetchFormulaUsageSuccess,
	initFormulaList,
	openDeleteConfirmationPopup,
	openDuplicateConfirmationPopup,
	refreshFormulaList,
	toggleFormulaSharedStatus,
} from './formula.actions';

@Injectable()
export class FormulaEffects {
	// ##########################################################
	// Business Effects
	// ##########################################################
	public formulaUsages$ = createEffect(() =>
		this.action$.pipe(
			ofType(fetchFormulaUsage),
			switchMap(() => this.formulaRequester.getFormulasUsages()),
			map((res) =>
				fetchFormulaUsageSuccess({
					elements: this.formulaParser.parseFormulaUsages(res),
					allUsageCount: this.formulaParser.getAllFormulaUsageCount(res),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchFormulaUsageFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	// ##########################################################
	// Business Effects
	// ##########################################################

	public initFormulaList$ = createEffect(() =>
		this.action$.pipe(
			ofType(initFormulaList),
			switchMap(() =>
				combineLatest([
					this.formulaRequester.getFormulaList(true),
					this.appSelector.getCurrentUserId$(),
				])
			),
			map(([response, currentUserId]) =>
				fetchFormulaListSuccess({
					formulaList: this.formulaParser.generateFormulaList(
						response,
						currentUserId
					),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchFormulaListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshFormulaList$ = createEffect(() =>
		this.action$.pipe(
			ofType(refreshFormulaList),
			switchMap(() =>
				combineLatest([
					this.formulaRequester.getFormulaList(false),

					this.appSelector.getCurrentUserId$(),
				])
			),
			map(([response, currentUserId]) =>
				fetchFormulaListSuccess({
					formulaList: this.formulaParser.generateFormulaList(
						response,
						currentUserId
					),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchFormulaListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public deleteFormula$ = createEffect(() =>
		this.action$.pipe(
			ofType(deleteFormula),
			switchMap((action) =>
				this.formulaRequester.deleteFormula(action.formulaId)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@formula.delete.success:`
				);
				return refreshFormulaList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@formula.delete.error:`
				);
				this.store.dispatch(deleteFormulaFail());
				return caught;
			})
		)
	);

	public bulkDeleteFormulas$ = createEffect(() =>
		this.action$.pipe(
			ofType(bulkDeleteFormula),
			switchMap((action) =>
				this.formulaRequester.deleteMany(action.formulaIds)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@formula.delete.success:`
				);
				return refreshFormulaList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@formula.delete.error:`
				);
				this.store.dispatch(deleteFormulaFail());
				return caught;
			})
		)
	);

	public duplicateFormula$ = createEffect(() =>
		this.action$.pipe(
			ofType(duplicateFormula),
			switchMap((action) =>
				this.formulaRequester.duplicateFormula(action.formulaId)
			),
			map(() => refreshFormulaList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(duplicateFormulaFail());
				return caught;
			})
		)
	);

	public toggleFormulaSharedStatus$ = createEffect(() =>
		this.action$.pipe(
			ofType(toggleFormulaSharedStatus),
			switchMap((action) =>
				this.formulaRequester.updateSharedStatus(
					action.formulaId,
					action.shared
				)
			),
			map(() => refreshFormulaList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(refreshFormulaList());
				return caught;
			})
		)
	);

	// ##########################################################
	// Confirmation popup effects
	// ##########################################################

	public openDeleteConfirmationPopup$ = createEffect(() =>
		this.action$.pipe(
			ofType(openDeleteConfirmationPopup),
			switchMap((action) => {
				const modalRef = this.modalService.openPopup<IFormulaActionModalData>(
					FormulaActionPopupComponent,
					{
						title: $localize`:i18n=@@formula.action.popup.delete.header:`,
						subTitle: '',
						formulaId: action.formulaId,
						element: action.formulaLabel,
						action: FormulaAction.Delete,
					},
					{
						disableClose: true,
					}
				);
				return combineLatest([of(action), modalRef.afterClosed()]);
			}),
			filter(([, modalResult]) => modalResult !== undefined),
			map(([action]) => {
				if (action.formulaId.length === 1) {
					return deleteFormula({
						formulaId: action.formulaId[0],
					});
				} else {
					return bulkDeleteFormula({
						formulaIds: action.formulaId,
					});
				}
			})
		)
	);

	public openDuplicateConfirmationPopup$ = createEffect(
		() =>
			this.action$.pipe(
				ofType(openDuplicateConfirmationPopup),
				switchMap((action) => {
					const modalRef = this.modalService.openPopup<IFormulaActionModalData>(
						FormulaActionPopupComponent,
						{
							title: $localize`:i18n=@@formula.action.popup.duplicate.header:`,
							subTitle: '',
							formulaId: action.formulaId,
							element: action.formulaLabel,
							action: FormulaAction.Duplicate,
						},
						{
							disableClose: true,
						}
					);
					return combineLatest([of(action), modalRef.afterClosed()]);
				}),
				filter(([, modalResult]) => modalResult !== undefined),
				map(([action]) => {
					action.formulaId.map((id) =>
						this.store.dispatch(
							duplicateFormula({
								formulaId: id,
							})
						)
					);
				})
			),
		{
			dispatch: false,
		}
	);

	public constructor(
		private readonly action$: Actions,
		private readonly formulaRequester: FormulaRequester,
		private readonly appSelector: AppSelector,
		private readonly store: Store,
		private readonly formulaParser: FormulaParser,
		private readonly modalService: AppModalService,
		private readonly ngCommService: NgCommService
	) {}
}
